import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { getControl, updateControl, getDocuments, getEvidence, uploadDocument, uploadEvidence, getLinkableItems, linkItemsToControl, markDocumentReviewed, markEvidenceReviewed, deleteDocument, deleteEvidence } from '@/services/grc';
import { 
  ArrowLeft, FileText, Shield, CheckCircle2, Clock, AlertCircle, 
  Download, Upload, Plus, Eye, Trash2, MoreVertical, XCircle,
  HourglassIcon, FileCheck, FileWarning, Loader2, ClipboardCheck,
  Search, BarChart3, Target, ListChecks, Link, Calendar, RefreshCw
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from 'sonner';

const ControlDetailPage = () => {
  const { controlId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // If we arrived via a link from the Controls list, `from` carries the filtered list URL
  const backTo = location.state?.from || '/grc/controls';
  const [control, setControl] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  
  // Upload dialog states
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadType, setUploadType] = useState('document'); // 'document' or 'evidence'
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    document_type: 'policy',
    review_frequency: 'monthly',
    owner: ''
  });

  // Link existing items
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkType, setLinkType] = useState('policy');
  const [linkableItems, setLinkableItems] = useState([]);
  const [linkSearch, setLinkSearch] = useState('');
  const [selectedLinkIds, setSelectedLinkIds] = useState([]);
  const [linkLoading, setLinkLoading] = useState(false);
  const [linkFrequency, setLinkFrequency] = useState('monthly');

  // Review
  const [reviewingId, setReviewingId] = useState(null);

  useEffect(() => {
    loadControlDetails();
  }, [controlId]);

  const loadControlDetails = async () => {
    try {
      setLoading(true);
      const controlData = await getControl(controlId);
      setControl(controlData);

      // Load related documents and evidence
      try {
        const [docsData, evidenceData] = await Promise.all([
          getDocuments({ control_id: controlId }),
          getEvidence({ control_id: controlId })
        ]);
        
        // Handle paginated response
        setDocuments(docsData?.items || docsData || []);
        setEvidence(evidenceData?.items || evidenceData || []);
      } catch (e) {
        console.error('Error loading documents/evidence:', e);
        setDocuments([]);
        setEvidence([]);
      }
    } catch (error) {
      console.error('Error loading control:', error);
      toast.error('Failed to load control details');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    try {
      setUpdating(true);
      await updateControl(controlId, { status: newStatus });
      setControl({ ...control, status: newStatus });
      toast.success('Control status updated successfully');
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update control status');
    } finally {
      setUpdating(false);
    }
  };

  const openUploadDialog = (type) => {
    setUploadType(type);
    setUploadForm({
      title: '',
      description: '',
      document_type: type === 'document' ? 'policy' : 'screenshot',
      review_frequency: 'monthly',
      owner: ''
    });
    setSelectedFile(null);
    setShowUploadDialog(true);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      if (!uploadForm.title) {
        setUploadForm({ ...uploadForm, title: file.name.split('.')[0] });
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }
    if (!uploadForm.title) {
      toast.error('Please enter a title');
      return;
    }

    try {
      setUploading(true);
      
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('title', uploadForm.title);
      formData.append('description', uploadForm.description);
      formData.append('control_id', controlId);
      formData.append('control_ids', JSON.stringify([controlId]));
      formData.append('owner', uploadForm.owner || '');
      formData.append('review_frequency', uploadForm.review_frequency || 'monthly');
      
      if (uploadType === 'document') {
        formData.append('document_type', uploadForm.document_type);
        await uploadDocument(formData);
        toast.success('Document uploaded successfully');
      } else {
        formData.append('evidence_type', uploadForm.document_type);
        await uploadEvidence(formData);
        toast.success('Evidence uploaded successfully');
      }
      
      setShowUploadDialog(false);
      loadControlDetails(); // Refresh the data
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(`Failed to upload ${uploadType}`);
    } finally {
      setUploading(false);
    }
  };

  const openLinkDialog = async (type) => {
    setLinkType(type);
    setSelectedLinkIds([]);
    setLinkSearch('');
    setLinkFrequency('monthly');
    setShowLinkDialog(true);
    try {
      setLinkLoading(true);
      const res = await getLinkableItems(controlId, type);
      setLinkableItems(res.items || []);
    } catch { toast.error('Failed to load items'); }
    finally { setLinkLoading(false); }
  };

  const searchLinkable = async (q) => {
    setLinkSearch(q);
    try {
      const res = await getLinkableItems(controlId, linkType, q);
      setLinkableItems(res.items || []);
    } catch {}
  };

  const handleLink = async () => {
    if (selectedLinkIds.length === 0) { toast.error('Select at least one item'); return; }
    try {
      await linkItemsToControl(controlId, { type: linkType, item_ids: selectedLinkIds, review_frequency: linkFrequency });
      toast.success(`Linked ${selectedLinkIds.length} item(s)`);
      setShowLinkDialog(false);
      loadControlDetails();
    } catch { toast.error('Failed to link'); }
  };

  const handleReview = async (id, type, frequency) => {
    try {
      setReviewingId(id);
      if (type === 'document') {
        await markDocumentReviewed(id, { review_frequency: frequency });
      } else {
        await markEvidenceReviewed(id, { review_frequency: frequency });
      }
      toast.success('Marked as reviewed');
      loadControlDetails();
    } catch { toast.error('Review failed'); }
    finally { setReviewingId(null); }
  };

  const isOverdue = (nextReviewDate) => {
    if (!nextReviewDate) return false;
    return new Date(nextReviewDate) < new Date();
  };

  const getQuarter = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const q = Math.ceil((d.getMonth() + 1) / 3);
    return `Q${q} ${d.getFullYear()}`;
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await deleteDocument(docId);
      toast.success('Document deleted');
      loadControlDetails();
    } catch { toast.error('Failed to delete document'); }
  };

  const handleDeleteEvidence = async (evId) => {
    if (!window.confirm('Delete this evidence?')) return;
    try {
      await deleteEvidence(evId);
      toast.success('Evidence deleted');
      loadControlDetails();
    } catch { toast.error('Failed to delete evidence'); }
  };

  const handleDownload = (item) => {
    const url = item.file_url || item.file_path;
    if (url) {
      window.open(url, '_blank');
    } else {
      toast.error('No file available for download');
    }
  };

  const handleViewItem = (item) => {
    const url = item.file_url || item.file_path;
    if (url) {
      window.open(url, '_blank');
    } else {
      toast.info(`${item.title} — ${item.description || 'No preview available'}`);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      implemented: { color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle2, label: 'Implemented' },
      in_progress: { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: Clock, label: 'In Progress' },
      not_implemented: { color: 'bg-gray-100 text-gray-800 border-gray-200', icon: AlertCircle, label: 'Not Implemented' },
      not_applicable: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: XCircle, label: 'N/A' }
    };
    const config = statusConfig[status] || statusConfig.not_implemented;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} border`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const getApprovalBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: HourglassIcon, label: 'Pending Upload' },
      uploaded: { color: 'bg-blue-100 text-blue-800', icon: FileCheck, label: 'Uploaded' },
      pending_review: { color: 'bg-orange-100 text-orange-800', icon: Clock, label: 'Pending Review' },
      pending_approval: { color: 'bg-purple-100 text-purple-800', icon: FileWarning, label: 'Pending Approval' },
      approved: { color: 'bg-green-100 text-green-800', icon: CheckCircle2, label: 'Approved' },
      rejected: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Rejected' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    return (
      <Badge className={config.color}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!control) {
    return (
      <div className="text-center py-12">
        <h3 className="text-xl font-semibold text-gray-700">Control not found</h3>
        <Button onClick={() => navigate(backTo)} className="mt-4">
          Back to Controls
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <Button onClick={() => navigate(backTo)} variant="outline" data-testid="back-to-controls-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Controls
        </Button>
        <div className="flex gap-2">
          <Button onClick={() => openLinkDialog('policy')} variant="outline" data-testid="link-policy-btn">
            <Link className="w-4 h-4 mr-2" /> Link Policy
          </Button>
          <Button onClick={() => openUploadDialog('document')} variant="outline">
            <Upload className="w-4 h-4 mr-2" /> Upload Document
          </Button>
          <Button onClick={() => openUploadDialog('evidence')} variant="outline">
            <Upload className="w-4 h-4 mr-2" /> Upload Evidence
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="text-2xl">{control.control_name}</CardTitle>
              <CardDescription className="mt-2">
                <span className="font-mono text-lg">{control.control_id}</span>
              </CardDescription>
            </div>
            {getStatusBadge(control.status)}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pb-4 border-b">
            <div>
              <p className="text-sm text-gray-500">Domain</p>
              <p className="font-medium">{control.domain}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Owner</p>
              <p className="font-medium">{control.owner}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Reviewer</p>
              <p className="font-medium">{control.reviewer || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Documents / Evidence</p>
              <p className="font-medium">{documents.length} / {evidence.length}</p>
              {(documents.length > 0 || evidence.length > 0) && (
                <p className="text-xs mt-0.5">
                  {(() => {
                    const allItems = [...documents, ...evidence];
                    const overdue = allItems.filter(i => isOverdue(i.next_review_date)).length;
                    const unapproved = allItems.filter(i => i.approval_status !== 'approved').length;
                    const allApproved = unapproved === 0 && allItems.length > 0;
                    const allCurrent = overdue === 0;
                    if (allApproved && allCurrent) {
                      return <span className="text-emerald-600 font-medium">Compliant - All approved & reviews current</span>;
                    }
                    const issues = [];
                    if (unapproved > 0) issues.push(<span key="u" className="text-orange-500">{unapproved} unapproved</span>);
                    if (overdue > 0) issues.push(<span key="o" className="text-red-500">{overdue} overdue</span>);
                    return <span className="flex items-center gap-1">{issues.reduce((a, b) => [a, ' | ', b])}</span>;
                  })()}
                </p>
              )}
            </div>
          </div>

          <div className="mt-4">
            <label className="text-sm font-medium text-gray-700">Update Status</label>
            <Select 
              value={control.status} 
              onValueChange={handleStatusUpdate}
              disabled={updating}
            >
              <SelectTrigger className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="not_implemented">Not Implemented</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="implemented">Implemented</SelectItem>
                <SelectItem value="not_applicable">Not Applicable</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="details" className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="methodology" data-testid="methodology-tab">Testing Methodology</TabsTrigger>
          <TabsTrigger value="mappings">Mappings</TabsTrigger>
          <TabsTrigger value="documents">Documents ({documents.length})</TabsTrigger>
          <TabsTrigger value="evidence">Evidence ({evidence.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Requirement Text</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 whitespace-pre-wrap">{control.requirement_text}</p>
            </CardContent>
          </Card>

          {control.objective && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Target className="w-5 h-5 text-indigo-600" />Objective</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 whitespace-pre-wrap">{control.objective}</p>
              </CardContent>
            </Card>
          )}

          {control.evidence_requirements && (
            <Card>
              <CardHeader>
                <CardTitle>Evidence Requirements</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 whitespace-pre-wrap">{control.evidence_requirements}</p>
              </CardContent>
            </Card>
          )}

          {control.success_criteria && control.success_criteria.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><ListChecks className="w-5 h-5 text-emerald-600" />Success Criteria</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {control.success_criteria.map((criterion, i) => (
                    <li key={i} className="flex items-start gap-3 p-2 rounded-lg bg-emerald-50 border border-emerald-100" data-testid={`success-criterion-${i}`}>
                      <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-600 flex-shrink-0" />
                      <span className="text-sm text-gray-700">{criterion}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Roles & Responsibilities</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between border-b pb-2">
                  <span className="text-gray-600">Owner:</span>
                  <span className="font-medium">{control.owner}</span>
                </div>
                {control.reviewer && (
                  <div className="flex justify-between border-b pb-2">
                    <span className="text-gray-600">Reviewer:</span>
                    <span className="font-medium">{control.reviewer}</span>
                  </div>
                )}
                {control.approver && (
                  <div className="flex justify-between border-b pb-2">
                    <span className="text-gray-600">Approver:</span>
                    <span className="font-medium">{control.approver}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Testing Methodology Tab */}
        <TabsContent value="methodology" className="space-y-4">
          {control.testing_methodology ? (
            <>
              {control.objective && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2"><Target className="w-5 h-5 text-indigo-600" />Objective</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700">{control.objective}</p>
                  </CardContent>
                </Card>
              )}

              {/* Evidence Collection */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><Search className="w-5 h-5 text-blue-600" />Evidence Collection</CardTitle>
                  <CardDescription>What evidence to gather for testing this control</CardDescription>
                </CardHeader>
                <CardContent>
                  <ol className="space-y-2">
                    {(control.testing_methodology.evidence || []).map((item, i) => (
                      <li key={i} className="flex items-start gap-3 p-3 rounded-lg bg-blue-50 border border-blue-100" data-testid={`evidence-item-${i}`}>
                        <span className="font-bold text-blue-600 text-sm mt-0.5 min-w-[24px]">{i + 1}.</span>
                        <span className="text-sm text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ol>
                </CardContent>
              </Card>

              {/* Analysis Steps */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><BarChart3 className="w-5 h-5 text-violet-600" />Analysis Steps</CardTitle>
                  <CardDescription>How to analyze the collected evidence</CardDescription>
                </CardHeader>
                <CardContent>
                  <ol className="space-y-2">
                    {(control.testing_methodology.analysis || []).map((item, i) => (
                      <li key={i} className="flex items-start gap-3 p-3 rounded-lg bg-violet-50 border border-violet-100" data-testid={`analysis-item-${i}`}>
                        <span className="font-bold text-violet-600 text-sm mt-0.5 min-w-[24px]">{i + 1}.</span>
                        <span className="text-sm text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ol>
                </CardContent>
              </Card>

              {/* Expected Output */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><ClipboardCheck className="w-5 h-5 text-amber-600" />Expected Output</CardTitle>
                  <CardDescription>Deliverables and reports from this assessment</CardDescription>
                </CardHeader>
                <CardContent>
                  <ol className="space-y-2">
                    {(control.testing_methodology.output || []).map((item, i) => (
                      <li key={i} className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-100" data-testid={`output-item-${i}`}>
                        <span className="font-bold text-amber-600 text-sm mt-0.5 min-w-[24px]">{i + 1}.</span>
                        <span className="text-sm text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ol>
                </CardContent>
              </Card>

              {/* Success Criteria */}
              {control.success_criteria && control.success_criteria.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2"><ListChecks className="w-5 h-5 text-emerald-600" />Success Criteria</CardTitle>
                    <CardDescription>Conditions that must be met for the control to pass</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {control.success_criteria.map((criterion, i) => (
                        <li key={i} className="flex items-start gap-3 p-3 rounded-lg bg-emerald-50 border border-emerald-100">
                          <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-600 flex-shrink-0" />
                          <span className="text-sm text-gray-700">{criterion}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <ClipboardCheck className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500 mb-2">No testing methodology defined for this control</p>
                <p className="text-sm text-gray-400">Testing methodology will be auto-assigned based on the control domain</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="mappings">
          <Card>
            <CardHeader>
              <CardTitle>Cross-Framework Mappings</CardTitle>
              <CardDescription>
                This control maps to the following frameworks and standards
              </CardDescription>
            </CardHeader>
            <CardContent>
              {control.mappings && control.mappings.length > 0 ? (
                <div className="space-y-3">
                  {control.mappings.map((mapping, index) => (
                    <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium">{mapping.framework}</p>
                        <p className="text-sm text-gray-600 font-mono">{mapping.control_id}</p>
                      </div>
                      <Badge variant="outline">{mapping.framework}</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No framework mappings available</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="documents">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Related Documents</CardTitle>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => openLinkDialog('document')} data-testid="link-doc-btn">
                    <Link className="w-4 h-4 mr-1" /> Link Existing
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openLinkDialog('policy')} data-testid="link-policy-tab-btn">
                    <Link className="w-4 h-4 mr-1" /> Link Policy
                  </Button>
                  <Button size="sm" onClick={() => openUploadDialog('document')}>
                    <Plus className="w-4 h-4 mr-1" /> Upload
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {documents.length > 0 ? (
                <div className="space-y-3">
                  {documents.map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50" data-testid={`doc-item-${doc.id}`}>
                      <div className="flex items-center space-x-4">
                        <div className={`p-2 rounded-lg ${doc.source_type === 'policy' ? 'bg-purple-100' : 'bg-blue-100'}`}>
                          <FileText className={`w-5 h-5 ${doc.source_type === 'policy' ? 'text-purple-600' : 'text-blue-600'}`} />
                        </div>
                        <div>
                          <p className="font-medium">{doc.title}</p>
                          <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                            {doc.source_type === 'policy' && <Badge variant="outline" className="text-[10px]">Policy</Badge>}
                            <span>{doc.file_name || doc.document_type}</span>
                            {doc.review_frequency && <span className="flex items-center gap-0.5"><Calendar className="w-3 h-3" /> {doc.review_frequency}</span>}
                            {doc.next_review_date && (
                              <span className={isOverdue(doc.next_review_date) ? 'text-red-500 font-medium' : 'text-green-600'}>
                                {isOverdue(doc.next_review_date) ? 'Overdue' : `Next: ${new Date(doc.next_review_date).toLocaleDateString()}`}
                              </span>
                            )}
                            {doc.last_reviewed_at && <span className="opacity-50">Reviewed: {new Date(doc.last_reviewed_at).toLocaleDateString()}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {getApprovalBadge(doc.approval_status || 'uploaded')}
                        <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => handleReview(doc.id, 'document', doc.review_frequency || 'monthly')}
                          disabled={reviewingId === doc.id} data-testid={`review-doc-${doc.id}`}>
                          {reviewingId === doc.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />} Review
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild><Button variant="ghost" size="sm"><MoreVertical className="w-4 h-4" /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleViewItem(doc)}><Eye className="w-4 h-4 mr-2" /> View</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDownload(doc)}><Download className="w-4 h-4 mr-2" /> Download</DropdownMenuItem>
                            <DropdownMenuItem className="text-red-600" onClick={() => handleDeleteDocument(doc.id)}><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-500 mb-4">No documents uploaded yet</p>
                  <div className="flex gap-2 justify-center">
                    <Button variant="outline" onClick={() => openLinkDialog('policy')}><Link className="w-4 h-4 mr-2" /> Link Policy</Button>
                    <Button variant="outline" onClick={() => openUploadDialog('document')}><Upload className="w-4 h-4 mr-2" /> Upload</Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="evidence">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Evidence</CardTitle>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => openLinkDialog('evidence')} data-testid="link-evidence-btn">
                    <Link className="w-4 h-4 mr-1" /> Link Existing
                  </Button>
                  <Button size="sm" onClick={() => openUploadDialog('evidence')}>
                    <Plus className="w-4 h-4 mr-1" /> Upload
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {evidence.length > 0 ? (
                <div className="space-y-3">
                  {evidence.map((ev) => (
                    <div key={ev.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50" data-testid={`evidence-item-${ev.id}`}>
                      <div className="flex items-center space-x-4">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <Shield className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium">{ev.title}</p>
                          <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                            <span className="flex items-center gap-0.5"><Calendar className="w-3 h-3" /> {ev.review_frequency || 'monthly'}</span>
                            {ev.next_review_date && (
                              <span className={isOverdue(ev.next_review_date) ? 'text-red-500 font-medium' : 'text-green-600'}>
                                {isOverdue(ev.next_review_date) ? 'Overdue' : `Next: ${new Date(ev.next_review_date).toLocaleDateString()}`}
                              </span>
                            )}
                            {ev.last_reviewed_at && <span className="opacity-50">Reviewed: {new Date(ev.last_reviewed_at).toLocaleDateString()}</span>}
                            {ev.next_review_date && <Badge variant="outline" className="text-[10px]">{getQuarter(ev.next_review_date)}</Badge>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {getApprovalBadge(ev.approval_status || 'uploaded')}
                        <Select defaultValue={ev.review_frequency || 'monthly'} onValueChange={(v) => handleReview(ev.id, 'evidence', v)}>
                          <SelectTrigger className="w-28 h-7 text-xs" data-testid={`freq-select-${ev.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="monthly">Monthly</SelectItem>
                            <SelectItem value="quarterly">Quarterly</SelectItem>
                            <SelectItem value="semi-annual">Bi-Annual</SelectItem>
                            <SelectItem value="annual">Annual</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => handleReview(ev.id, 'evidence', ev.review_frequency || 'monthly')}
                          disabled={reviewingId === ev.id} data-testid={`review-ev-${ev.id}`}>
                          {reviewingId === ev.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />} Review
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild><Button variant="ghost" size="sm"><MoreVertical className="w-4 h-4" /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleViewItem(ev)}><Eye className="w-4 h-4 mr-2" /> View</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDownload(ev)}><Download className="w-4 h-4 mr-2" /> Download</DropdownMenuItem>
                            <DropdownMenuItem className="text-red-600" onClick={() => handleDeleteEvidence(ev.id)}><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Shield className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-500 mb-4">No evidence uploaded yet</p>
                  <div className="flex gap-2 justify-center">
                    <Button variant="outline" onClick={() => openLinkDialog('evidence')}><Link className="w-4 h-4 mr-2" /> Link Existing</Button>
                    <Button variant="outline" onClick={() => openUploadDialog('evidence')}><Upload className="w-4 h-4 mr-2" /> Upload</Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Upload {uploadType === 'document' ? 'Document' : 'Evidence'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>File *</Label>
              <div 
                className="mt-2 border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-blue-500 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                {selectedFile ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileText className="w-8 h-8 text-blue-500" />
                    <div className="text-left">
                      <p className="font-medium">{selectedFile.name}</p>
                      <p className="text-sm text-gray-500">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                    </div>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                    <p className="text-sm text-gray-500">Click to select a file</p>
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleFileSelect}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.txt"
              />
            </div>
            <div>
              <Label>Title *</Label>
              <Input
                value={uploadForm.title}
                onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                placeholder="Enter title"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={uploadForm.description}
                onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                placeholder="Enter description"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type</Label>
                <Select 
                  value={uploadForm.document_type} 
                  onValueChange={(v) => setUploadForm({ ...uploadForm, document_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {uploadType === 'document' ? (
                      <>
                        <SelectItem value="policy">Policy</SelectItem>
                        <SelectItem value="procedure">Procedure</SelectItem>
                        <SelectItem value="standard">Standard</SelectItem>
                        <SelectItem value="guideline">Guideline</SelectItem>
                        <SelectItem value="report">Report</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </>
                    ) : (
                      <>
                        <SelectItem value="screenshot">Screenshot</SelectItem>
                        <SelectItem value="config">Configuration</SelectItem>
                        <SelectItem value="log">Log File</SelectItem>
                        <SelectItem value="report">Report</SelectItem>
                        <SelectItem value="certificate">Certificate</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
              {uploadType === 'evidence' && (
                <div>
                  <Label>Review Frequency</Label>
                  <Select 
                    value={uploadForm.review_frequency} 
                    onValueChange={(v) => setUploadForm({ ...uploadForm, review_frequency: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="semi-annual">Bi-Annual</SelectItem>
                      <SelectItem value="annual">Annual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              {uploadType === 'document' && (
                <div>
                  <Label>Review Frequency</Label>
                  <Select 
                    value={uploadForm.review_frequency} 
                    onValueChange={(v) => setUploadForm({ ...uploadForm, review_frequency: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="semi-annual">Bi-Annual</SelectItem>
                      <SelectItem value="annual">Annual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div>
              <Label>Owner</Label>
              <Input
                value={uploadForm.owner}
                onChange={(e) => setUploadForm({ ...uploadForm, owner: e.target.value })}
                placeholder="Document owner"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)} disabled={uploading}>
              Cancel
            </Button>
            <Button onClick={handleUpload} disabled={uploading || !selectedFile || !uploadForm.title}>
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Existing Items Dialog */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent className="max-w-2xl w-[90vw] max-h-[85vh] flex flex-col" data-testid="link-dialog">
          <DialogHeader>
            <DialogTitle>
              Link {linkType === 'policy' ? 'Approved Policy' : linkType === 'document' ? 'Approved Document' : 'Approved Evidence'}
            </DialogTitle>
            <p className="text-xs text-muted-foreground">Only approved items are shown. Select items and choose a review frequency.</p>
          </DialogHeader>
          <div className="space-y-3 flex-1 min-h-0">
            <div className="flex gap-2">
              <Input placeholder={`Search ${linkType}s...`} value={linkSearch} onChange={(e) => searchLinkable(e.target.value)} className="flex-1" data-testid="link-search-input" />
              <Select value={linkFrequency} onValueChange={setLinkFrequency}>
                <SelectTrigger className="w-36" data-testid="link-freq-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="semi-annual">Bi-Annual</SelectItem>
                  <SelectItem value="annual">Annual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {linkLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div>
            ) : linkableItems.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-sm opacity-50">No approved {linkType}s found</p>
                <p className="text-xs opacity-30 mt-1">{linkType === 'policy' ? 'Approve policies first in the Policies page' : 'Approve items before linking'}</p>
              </div>
            ) : (
              <div className="space-y-1.5 overflow-y-auto max-h-[45vh] pr-1">
                {linkableItems.map(item => (
                  <div key={item.id} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${selectedLinkIds.includes(item.id) ? 'border-primary bg-primary/5 ring-1 ring-primary/20' : 'hover:bg-muted/50'}`}
                    onClick={() => setSelectedLinkIds(prev => prev.includes(item.id) ? prev.filter(x => x !== item.id) : [...prev, item.id])}
                    data-testid={`link-item-${item.id}`}>
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${selectedLinkIds.includes(item.id) ? 'bg-primary border-primary' : 'border-gray-300'}`}>
                      {selectedLinkIds.includes(item.id) && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.title}</p>
                      <p className="text-xs opacity-50 truncate">{item.description || item.framework || item.document_type || ''}</p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {item.owner && <span className="text-[10px] opacity-40">{item.owner}</span>}
                      <Badge className="text-[10px] bg-emerald-500">Approved</Badge>
                      {item.version && <Badge variant="secondary" className="text-[10px]">v{item.version}</Badge>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter className="border-t pt-3">
            <div className="flex items-center gap-2 mr-auto text-xs text-muted-foreground">
              {selectedLinkIds.length > 0 && <span>{selectedLinkIds.length} selected</span>}
            </div>
            <Button variant="outline" onClick={() => setShowLinkDialog(false)}>Cancel</Button>
            <Button onClick={handleLink} disabled={selectedLinkIds.length === 0} data-testid="confirm-link-btn">
              <Link className="w-4 h-4 mr-2" /> Link {selectedLinkIds.length} item{selectedLinkIds.length !== 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ControlDetailPage;
